/**
 * Phase1PrepGroupRow.tsx
 * The COLLAPSED aggregate header for a consolidated prep step in the Phase-1
 * task table (BUG-2026-0165, 2026-07-26): N per-line Cutting/Grinding/
 * Conformity rows render as ONE line — "cutting is cutting" (Adin) — with the
 * summed test count + machine time, the overall date span, and the machine.
 * Expanding reveals the real member rows (rendered by Phase1Panel with the
 * ordinary Phase1TaskRow), where per-line actions — date nudges, the sticky
 * per-test opt-out delete — stay fully available. DISPLAY-only: the tasks and
 * bookings underneath are untouched.
 *
 * Cell order mirrors PHASE1_TASK_COLUMNS. Colors via theme tokens.
 */

import React from 'react';
import { Box, Chip, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCutIcon from '@mui/icons-material/ContentCut';

import type { PrepGroup } from './prepRollup';
import { prepBreakdownLabel, prepTotalsLabel } from './prepRollup';
import { computeTaskDurationDays } from './taskDuration';

export interface Phase1PrepGroupRowProps {
  group: PrepGroup;
  rowNumber: number;
  holidays: string[];
  expanded: boolean;
  onToggle: () => void;
}

function fmt(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
}

export const Phase1PrepGroupRow: React.FC<Phase1PrepGroupRowProps> = ({
  group, rowNumber, holidays, expanded, onToggle,
}) => {
  const theme = useTheme();
  const duration = computeTaskDurationDays(group.startDate, group.endDate, holidays, 'attended');
  const breakdown = prepBreakdownLabel(group);

  return (
    <TableRow
      hover
      onClick={onToggle}
      sx={{
        cursor: 'pointer',
        backgroundColor: alpha(theme.schedule.machineTime, 0.05),
        '& td': { borderBottom: `1px solid ${theme.border.color}` },
      }}
      aria-expanded={expanded}
    >
      <TableCell align="center" sx={{ py: 0.75, px: 1.5, fontSize: '0.78rem', color: 'text.secondary' }}>
        {rowNumber}
      </TableCell>
      <TableCell sx={{ py: 0.75, px: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
          <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary', transition: 'transform 150ms', transform: expanded ? 'none' : 'rotate(-90deg)', flexShrink: 0 }} />
          <ContentCutIcon sx={{ fontSize: 13, color: theme.schedule.machineTime, flexShrink: 0 }} />
          <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{group.name}</Typography>
          <Tooltip title={`One line per prep step — covers ${group.members.length} tests: ${breakdown}. Expand for the per-test rows (move dates, remove a test's prep).`} arrow>
            <Chip
              size="small"
              label={`${group.members.length} tests`}
              sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, backgroundColor: alpha(theme.schedule.machineTime, 0.14), color: theme.schedule.machineTime }}
            />
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell align="center" sx={{ py: 0.75, fontSize: '0.8rem', fontWeight: 700 }}>
        {group.totalTests ?? '—'}
      </TableCell>
      <TableCell align="center" sx={{ py: 0.75, fontSize: '0.78rem', color: 'text.secondary' }}>
        {/* Machine Qty is a per-row control — expand to edit a member. */}
        —
      </TableCell>
      <TableCell sx={{ py: 0.75, px: 1.5, fontSize: '0.78rem' }}>
        {group.machineLabels.length ? group.machineLabels.join(', ') : '—'}
      </TableCell>
      <TableCell align="center" sx={{ py: 0.75, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{fmt(group.startDate)}</TableCell>
      <TableCell align="center" sx={{ py: 0.75, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>{fmt(group.endDate)}</TableCell>
      <TableCell align="center" sx={{ py: 0.75, fontSize: '0.78rem' }}>{duration ?? '—'}</TableCell>
      <TableCell align="center" sx={{ py: 0.75, whiteSpace: 'nowrap' }}>
        <Tooltip title={breakdown} arrow>
          <Typography component="span" sx={{ fontSize: '0.76rem', fontWeight: 600 }}>{prepTotalsLabel(group)}</Typography>
        </Tooltip>
      </TableCell>
      <TableCell align="center" sx={{ py: 0.75, fontSize: '0.66rem', color: 'text.secondary' }}>
        {expanded ? 'collapse' : 'expand'}
      </TableCell>
    </TableRow>
  );
};
